'use client';

import React, { useCallback, useState } from 'react';
import Link from 'next/link';

import CloseIcon from '@mui/icons-material/Close';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import { useSurveyLastEmail } from '@/hooks/useSurveyLastEmail';
import { useSurveyResponse } from '@/hooks/useSurveyResponse';

export const SURVEY_PARTIAL_STORAGE_KEY = 'survey_radar_2026_partial';
const SURVEY_BANNER_DISMISSED_KEY = 'survey_radar_2026_banner_dismissed';

export interface SurveyPartialState {
  email: string;
  trail: 'tech' | 'finance';
  inviteSent?: boolean;
}

function getInviteSent(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const raw = localStorage.getItem(SURVEY_PARTIAL_STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as SurveyPartialState;
    return parsed?.inviteSent === true;
  } catch {
    return false;
  }
}

function getBannerDismissed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(SURVEY_BANNER_DISMISSED_KEY) === 'true';
  } catch {
    return false;
  }
}

const RADAR_SLUG = 'radar-da-nuvem-2026';

function trackToTrail(track: string | null): 'tech' | 'finance' | null {
  if (track === 'tech') return 'tech';
  if (track === 'finance') return 'finance';
  return null;
}

export function SurveyProgressBanner() {
  const [dismissed, setDismissed] = useState(() => getBannerDismissed());
  const lastEmailQuery = useSurveyLastEmail(RADAR_SLUG);
  const email = lastEmailQuery.data ?? null;
  const responseQuery = useSurveyResponse(RADAR_SLUG);
  const response = responseQuery.data?.[0] ?? null;
  const techStatus = response?.track_progress?.tech?.status ?? 'not_started';
  const financeStatus = response?.track_progress?.finance?.status ?? 'not_started';
  const completedTrail =
    techStatus === 'completed' && financeStatus !== 'completed'
      ? 'tech'
      : financeStatus === 'completed' && techStatus !== 'completed'
        ? 'finance'
        : null;

  const isPartial = response != null && completedTrail != null;
  const trail = completedTrail ?? (response ? trackToTrail(response.track) : null);
  const inviteSent = getInviteSent();
  const state: SurveyPartialState | null =
    isPartial && response && trail && email
      ? { email, trail, inviteSent }
      : null;

  const handleClose = useCallback(() => {
    setDismissed(true);
    try {
      localStorage.setItem(SURVEY_BANNER_DISMISSED_KEY, 'true');
    } catch {
      // ignore
    }
  }, []);

  if (lastEmailQuery.isLoading || responseQuery.isLoading) return null;
  if (!state || dismissed) return null;

  const isInviteSent = state.inviteSent === true;

  const variant = isInviteSent
    ? 'invite'
    : state.trail === 'tech'
      ? 'tech'
      : 'finance';

  const title = 'Seu benchmark está 50% completo';
  const description =
    variant === 'invite'
      ? 'Assim que seu par responder, o dashboard completo será liberado.'
      : variant === 'tech'
        ? 'Faltam apenas os dados da trilha Financeira para desbloquear seu relatório de eficiência 360°.'
        : 'Faltam apenas os dados da trilha Técnica para desbloquear seu relatório de eficiência 360°.';

  const buttonLabel =
    variant === 'invite'
      ? 'Reenviar Convite'
      : variant === 'tech'
        ? 'Convidar Financeiro →'
        : 'Convidar Tech Lead →';

  const inviteTitle =
    variant === 'invite' ? 'Convite enviado! Aguardando resposta...' : title;

  const Icon = variant === 'invite' ? MailOutlineIcon : ScheduleIcon;

  return (
    <Box
      component="aside"
      role="banner"
      aria-label="Progresso da pesquisa Radar da Nuvem"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        py: 1.5,
        px: 2,
        backgroundColor: (theme) =>
          theme.palette.warning?.light ?? theme.palette.grey[200],
        borderBottom: 1,
        borderColor: 'divider',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 2,
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
        <Icon color="action" fontSize="small" />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant='title-small'>
            {inviteTitle}
          </Typography>
          <Typography variant='body-medium' color='text.secondary'>
            {description}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button
          component={Link}
          href={
            state.email
              ? `/survey/radar-da-nuvem-2026?email=${encodeURIComponent(state.email)}`
              : '/survey/radar-da-nuvem-2026'
          }
          variant="contained"
          size="small"
          color="primary"
        >
          {buttonLabel}
        </Button>
        <IconButton
          size="small"
          onClick={handleClose}
          aria-label="Fechar aviso"
          sx={{ ml: 0.5 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
}
